/**
 * Retry HTTP Interceptor
 *
 * Implements automatic retry with exponential backoff for transient failures.
 * Only retries GET requests to ensure idempotency.
 *
 * Features:
 * - Exponential backoff: delay = min(initialDelay * 2^attempt, maxDelay)
 * - Respects Retry-After header for 429 responses
 * - Only retries status codes: 408, 429, 500, 502, 503, 504
 * - Max 3 retries by default
 * - Non-idempotent methods (POST, PUT, DELETE) are never retried
 *
 * @see VEO-086 Sprint 2 Plan
 */
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Retry configuration
 */
interface RetryConfig {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
}

/**
 * Default retry configuration
 */
const DEFAULT_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Check if request method is idempotent and safe to retry
 */
function isRetryableMethod(method: string): boolean {
  return method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
}

/**
 * Check if HTTP status code is retryable
 */
function isRetryableStatus(status: number, config: RetryConfig): boolean {
  return config.retryableStatusCodes.includes(status);
}

/**
 * Extract Retry-After header value in seconds
 */
function getRetryAfterSeconds(error: HttpErrorResponse): number | null {
  const retryAfter = error.headers.get('Retry-After');
  if (!retryAfter) {
    return null;
  }

  // Parse as number (seconds)
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000; // Convert to milliseconds
  }

  // Parse as HTTP date
  const date = new Date(retryAfter);
  if (!isNaN(date.getTime())) {
    return Math.max(0, date.getTime() - Date.now());
  }

  return null;
}

/**
 * Calculate exponential backoff delay
 */
function calculateDelay(
  retryCount: number,
  error: HttpErrorResponse,
  config: RetryConfig
): number {
  // For 429 (rate limit), respect Retry-After header
  if (error.status === 429) {
    const retryAfter = getRetryAfterSeconds(error);
    if (retryAfter !== null) {
      return Math.min(retryAfter, config.maxDelay);
    }
  }

  // Exponential backoff: initialDelay * (multiplier ^ retryCount)
  const delay = config.initialDelay * Math.pow(config.backoffMultiplier, retryCount - 1);
  return Math.min(delay, config.maxDelay);
}

/**
 * Functional HTTP interceptor for automatic retry with exponential backoff
 */
export const retryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const config = DEFAULT_CONFIG;

  // Only retry idempotent methods
  if (!isRetryableMethod(req.method)) {
    return next(req);
  }

  // Implement retry logic
  return next(req).pipe(
    retry({
      count: config.maxRetries,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        // Only retry HTTP errors with retryable status codes
        if (!(error instanceof HttpErrorResponse) || !isRetryableStatus(error.status, config)) {
          // Non-retryable error - throw immediately
          throw error;
        }

        const delay = calculateDelay(retryCount, error, config);

        // Log retry attempt in development mode
        if (!environment.production) {
          console.log(
            `[RetryInterceptor] Retry ${retryCount}/${config.maxRetries} for ${req.method} ${req.url}`,
            {
              status: error.status,
              delay: `${delay}ms`,
              retryAfter: error.headers.get('Retry-After'),
            }
          );
        }

        // Return timer observable that delays the retry
        return timer(delay);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      // Log final failure in development mode
      if (!environment.production && error instanceof HttpErrorResponse) {
        console.error(
          `[RetryInterceptor] All retries exhausted for ${req.method} ${req.url}`,
          {
            status: error.status,
            message: error.message,
          }
        );
      }
      return throwError(() => error);
    })
  );
};
